import React, { useContext, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import classes from './ReviewSection.module.scss';
import { modalContext } from '@/context/modal.context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReviewListItem from './ReviewListItem';
import Button from '@/components/Button/Button';
import Review from '@/utils/api/odrs/review';
import ODASReview from '@/utils/api/odas/review';
import { ratingHelpers } from '@/utils/helpers';
import t from '@/utils/Language/Review-translations';
import { capitalize } from 'lodash';

const initialDisplayCount = 5;

const ReviewSection = ({ resource, account, onCountLoaded }) => {
  const modal = useContext(modalContext);
  const [reviews, setReviews] = useState([]);
  const [overallRating, setOverallRating] = useState(resource.rating);
  const [ratingTags, setRatingTags] = useState(resource.rating_tags ?? {});
  const [userRatingTags, setUserRatingTags] = useState({});
  const [userReview, setUserReview] = useState({});
  const [fetching, setFetching] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const displayedItems = isExpanded ? reviews : reviews.slice(0, initialDisplayCount);
  const shouldDisplayButton = reviews.length > initialDisplayCount;

  useEffect(() => {
    getReviewsData();
  }, [resource.id]);

  useEffect(() => {
    if (account?.data?.id) {
      const fetchUserReviewData = async () => {
        const [review, tags] = await Promise.all([
          Review.getUserReview(account?.data?.id, resource).catch(() => ({ data: {} })),
          ODASReview.getCurrentUserRatingTags(resource)
        ]);
        setUserReview(review.data);
        setUserRatingTags(tags?.data ?? {});
      };
      fetchUserReviewData();
    }
  }, [account?.data?.id, resource.id]);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const getReviewsData = async () => {
    const reviewCount = 20;
    try {
      const response = await Review.getReviews(resource, reviewCount);
      const initialComments = resource?.opportunity_comments ?? [];
      const reviewData = [...(response?.data?.comments ?? []), ...initialComments];
      setReviews(reviewData);
      if (typeof onCountLoaded === 'function') onCountLoaded(reviewData.length);
      // Recompute the displayed rating as the average of the fetched reviews that carry one;
      // fall back to the resource's stored rating (the initial state) when none do.
      const rated = reviewData.filter((r) => r.related_overall_rating > 0);
      if (rated.length > 0) {
        setOverallRating(rated.reduce((sum, r) => sum + r.related_overall_rating, 0) / rated.length);
      }
    } finally {
      setFetching(false);
    }
  };

  const updateReview = (data) => {
    getReviewsData();
    if (data?.comment?.id) {
      setUserReview(data.comment);
    }
    const newUserTags = data?.rating_tags ?? {};
    setRatingTags((prev) => {
      const updated = { ...prev };
      // Decrement tags the user previously had but no longer selected
      Object.keys(userRatingTags).forEach((key) => {
        if (!newUserTags[key]) {
          updated[key] = Math.max(0, (updated[key] || 0) - 1);
        }
      });
      // Increment tags the user newly selected
      Object.keys(newUserTags).forEach((key) => {
        if (!userRatingTags[key]) {
          updated[key] = (updated[key] || 0) + 1;
        }
      });
      return updated;
    });
    setUserRatingTags(newUserTags);
  };

  const reviewThisService = () => {
    if (account.state.isLoggedIn) {
      modal.enqueueModal({
        name: 'NewReviewModal',
        resource: resource,
        user: account.data,
        previousRating: userReview?.related_overall_rating ?? 0,
        previousReviewContent: userReview?.content ?? '',
        previousRatingTags: userRatingTags,
        updateReviewCallback: updateReview
      });
    } else {
      modal.enqueueModal({ name: 'SignupModal' }, { forceOpen: true });
    }
  };

  return (
    <div className={classes.reviewSectionContainer} id="org-review">
      <div className={classes.reviewSectionPanel}>
        <div className={classes.reviewSectionHeader}>
          <div className={classes.reviewSectionTitleWrapper}>
            <FontAwesomeIcon icon={['fa-regular', 'fa-star']} className={classes.reviewSectionIcon} />
            <h2 className={classes.reviewSectionTitle}>{t('Reviews')}</h2>
          </div>
          <Button
            type="link"
            text={userReview?.id ? t('Edit your review') : t('Add Review')}
            icon={{ style: 'fas', font: 'plus', position: 'left' }}
            additionalClasses={classes.reviewHeaderBtn}
            onButtonClick={() => reviewThisService()}
          />
        </div>
        <div className={classes.reviewSectionContent}>
          {(reviews.length > 0 || userReview?.id) && (
            <div className={classes.ratingTagsContainer}>
              <div className={classes.ratingTagsBlock}>
                <div className={classes.ratingSummary}>
                  <span className={classes.ratingValue}>{overallRating?.toFixed(1)}</span>
                  {reviews.length > 0 && (
                    <span className={classes.ratingCount}>
                      {reviews.length === 1 ? t('Based on 1 review') : t('Based on ${count} reviews', { count: reviews.length })}
                    </span>
                  )}
                </div>
                <div className={classes.ratingTags}>
                  {Object.entries(ratingTags).map(
                    ([key, value]) =>
                      value > 0 && (
                        <div className={classes.ratingTagsItem} key={key}>
                          <FontAwesomeIcon icon={['fas', 'check']} className={classes.ratingTagIcon} />
                          <span className={classes.ratingTagText}>
                            {capitalize(t(ratingHelpers.getRatingTag(key)?.good))} ({value})
                          </span>
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={classes.reviewLayout}>
            <Row className="gx-4">
              {displayedItems.map((el, index) => (
                <Col lg={6} key={el.id ?? index}>
                  <ReviewListItem reviewItem={el} resource={resource} isOwnReview={!!userReview?.id && el.id === userReview.id} />
                </Col>
              ))}
            </Row>
            {!fetching && reviews.length === 0 && !userReview?.id && (
              <div className={classes.reviewListEmpty}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t('Have you used this resource___')
                  }}
                ></p>
              </div>
            )}
            {shouldDisplayButton && (
              <div className={classes.reviewShowMoreWrapper}>
                <Button
                  type="link"
                  additionalClasses={classes.reviewShowMoreBtn}
                  text={isExpanded ? t('Hide reviews') : t('Show all reviews')}
                  onButtonClick={toggleExpansion}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSection;
